import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getMswVisitNoteForm,
  updateMswVisitNoteForm,
} from "../../../../../services/patientService";
import {
  BrainIcon,
  CalendarIcon,
  DeleteIcon,
  FileCheckIcon,
  HelpingIcon,
  HomeIcon,
  MedicalNotesIcon,
  NotesIcon,
  PlusIcon,
  ShieldCheckIcon,
  TargetIcon,
  UsersIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  MSW_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ABILITY_TO_ASSIST_OPTIONS,
  ADVANCE_DIRECTIVE_ROWS,
  ADVANCE_DIRECTIVE_YES_NO,
  CAREGIVER_AVAILABILITY_OPTIONS,
  COMMUNITY_RESOURCES_OPTIONS,
  COPING_MECHANISMS_OPTIONS,
  HOME_SAFETY_OPTIONS,
  INTERVENTIONS_PROVIDED_OPTIONS,
  MENTAL_STATUS_OPTIONS,
  MOOD_EMOTIONAL_OPTIONS,
  MSW_VISIT_ARRAY_KEYS,
  PROGRESS_GOAL_STATUS_CLASS,
  PROGRESS_GOAL_STATUS_OPTIONS,
  RESIDENCE_TYPE_OPTIONS,
  SOCIAL_HISTORY_DYNAMICS_OPTIONS,
  STRESS_STRAIN_OPTIONS,
  addProgressGoal,
  advanceDirectiveDisplay,
  advanceDirectiveFromDisplay,
  emptyMswVisitPayload,
  normalizeAdvanceDirectives,
  normalizeProgressGoals,
  removeProgressGoal,
  serializeProgressGoals,
  toggleInList,
} from "./mswVisitNoteContent";
import MswNotesCommonHeader from "./MswNotesCommonHeader";
import {
  normalizeMswCommonHeaderState,
  resolveMswNotesCommonHeader,
  withNormalizedMswCommonHeader,
} from "./mswNotesCommonHeaderUtils";
import {
  assertNurseVisitMetaForFlag,
  buildNurseVisitMetaProps,
  pickNurseVisitMeta,
} from "../nurseNotes/nurseVisitCommon";

const VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptyMswVisitPayload(),
  visitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const ChipMultiSelect = ({ options, values, canEdit, onToggle, name }) => {
  const selected = Array.isArray(values) ? values : [];
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label={name}>
      {options.map((opt) => {
        const checked = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            disabled={!canEdit}
            onClick={() => canEdit && onToggle(opt)}
            className={`inline-flex items-center px-3 py-1.5 rounded-md border text-[12px] font-medium transition-colors ${
              checked
                ? "border-indigo-300 bg-indigo-50 text-indigo-800"
                : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
            } ${canEdit ? "cursor-pointer" : "cursor-default opacity-90"}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
};

ChipMultiSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  values: PropTypes.arrayOf(PropTypes.string),
  canEdit: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  name: PropTypes.string,
};

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      compact
      className="min-w-0 w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const MswVisitNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyMswVisitPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickNurseVisitMeta(loadedPayload, data),
        visitDate: data.visitDate || loadedPayload.visitDate || today(),
        commonHeader: normalizeMswCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        advanceDirectives: normalizeAdvanceDirectives(
          loadedPayload.advanceDirectives || base.advanceDirectives,
        ),
        progressGoals: normalizeProgressGoals(loadedPayload.progressGoals, {
          minCount: 1,
        }),
        reasonForVisit: loadedPayload.reasonForVisit || "",
        residenceType: loadedPayload.residenceType || "",
        livesWith: loadedPayload.livesWith || "",
        homeSafetyConcerns: loadedPayload.homeSafetyConcerns || "",
        caregiverAvailability: loadedPayload.caregiverAvailability || "",
        abilityToAssist: loadedPayload.abilityToAssist || "",
        stressStrainIndicators: loadedPayload.stressStrainIndicators || "",
        financialBarriersToCare: loadedPayload.financialBarriersToCare || "",
        patientCaregiverEducation:
          loadedPayload.patientCaregiverEducation || "",
        planFrequency: loadedPayload.planFrequency || "",
        nextScheduledDate: loadedPayload.nextScheduledDate || "",
        additionalNotes: loadedPayload.additionalNotes || "",
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      MSW_VISIT_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : [];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveMswNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "MSW Visit Note",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            visitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyMswVisitPayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getMswVisitNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveMswNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const setAdvanceDirective = (key, displayLabel) => {
    setValues((prev) => ({
      ...prev,
      advanceDirectives: {
        ...normalizeAdvanceDirectives(prev.advanceDirectives),
        [key]: advanceDirectiveFromDisplay(displayLabel),
      },
    }));
  };

  const updateProgressGoal = (goalId, patch) => {
    setValues((prev) => ({
      ...prev,
      progressGoals: (prev.progressGoals || []).map((row) =>
        row.id === goalId ? { ...row, ...patch } : row,
      ),
    }));
  };

  const handleAddProgressGoal = () => {
    setValues((prev) => ({
      ...prev,
      progressGoals: addProgressGoal(prev.progressGoals),
    }));
  };

  const handleRemoveProgressGoal = (goalId) => {
    setValues((prev) => ({
      ...prev,
      progressGoals: removeProgressGoal(prev.progressGoals, goalId, {
        minCount: 1,
      }),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        MSW_FORM_EDITOR_ROLES,
        "View only — only MSW can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      visitDate,
      ...mswFields
    } = values;

    const nestedPayload = withNormalizedMswCommonHeader({
      ...mswFields,
      advanceDirectives: normalizeAdvanceDirectives(values.advanceDirectives),
      progressGoals: serializeProgressGoals(values.progressGoals),
      reasonForVisit: values.reasonForVisit || "",
      residenceType: values.residenceType || "",
      livesWith: values.livesWith || "",
      homeSafetyConcerns: values.homeSafetyConcerns || "",
      caregiverAvailability: values.caregiverAvailability || "",
      abilityToAssist: values.abilityToAssist || "",
      stressStrainIndicators: values.stressStrainIndicators || "",
      financialBarriersToCare: values.financialBarriersToCare || "",
      patientCaregiverEducation: values.patientCaregiverEducation || "",
      planFrequency: values.planFrequency || "",
      nextScheduledDate: values.nextScheduledDate
        ? formatDateForInput(values.nextScheduledDate)
        : "",
      additionalNotes: values.additionalNotes || "",
    });

    MSW_VISIT_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      visitDate: visitDate ? formatDateForInput(visitDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertNurseVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateMswVisitNoteForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.visitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const directives = normalizeAdvanceDirectives(values.advanceDirectives);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            MSW Visit Note
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home health clinical documentation for medical social work follow-up
            visits.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth || form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Visit Date"
          dateValue={displayDateUS(values.visitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Visit Date
                </div>
                <InputDatePicker
                  name="visitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.visitDate) || ""}
                  onChange={(event) => setValue("visitDate", event.target.value)}
                  max={VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildNurseVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <MswNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={NotesIcon} title="7. Reason for Visit">
          <textarea
            rows={3}
            className={textareaClass}
            value={values.reasonForVisit || ""}
            disabled={!canEdit}
            placeholder="Describe the reason for this visit…"
            onChange={(e) => setValue("reasonForVisit", e.target.value)}
          />
        </Section>

        <Section icon={HelpingIcon} title="8. Psychosocial Assessment">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <SubHeading>Coping Mechanisms</SubHeading>
              <CheckboxGrid
                options={COPING_MECHANISMS_OPTIONS}
                values={values.copingMechanisms}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("copingMechanisms", opt)}
                columns={1}
              />
            </div>
            <div>
              <SubHeading>Social History / Dynamics</SubHeading>
              <CheckboxGrid
                options={SOCIAL_HISTORY_DYNAMICS_OPTIONS}
                values={values.socialHistoryDynamics}
                canEdit={canEdit}
                onToggle={(opt) =>
                  toggleListValue("socialHistoryDynamics", opt)
                }
                columns={1}
              />
            </div>
          </div>
        </Section>

        <Section icon={BrainIcon} title="9. Mental & Emotional">
          <div className="space-y-3">
            <div>
              <SubHeading>Mental Status</SubHeading>
              <ChipMultiSelect
                name="mentalStatus"
                options={MENTAL_STATUS_OPTIONS}
                values={values.mentalStatus}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("mentalStatus", opt)}
              />
            </div>
            <div>
              <SubHeading>Mood / Emotional</SubHeading>
              <ChipMultiSelect
                name="moodEmotional"
                options={MOOD_EMOTIONAL_OPTIONS}
                values={values.moodEmotional}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("moodEmotional", opt)}
              />
            </div>
          </div>
        </Section>

        <Section icon={HomeIcon} title="10. Living Environment & Support">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Residence Type">
              <select
                className={fieldClass}
                value={values.residenceType || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("residenceType", e.target.value)}
              >
                <option value="">Select…</option>
                {RESIDENCE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Lives With">
              <input
                type="text"
                className={fieldClass}
                value={values.livesWith || ""}
                disabled={!canEdit}
                placeholder="e.g. Spouse, daughter…"
                onChange={(e) => setValue("livesWith", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-3">
            <SubHeading>Home Safety Concerns</SubHeading>
            <ExclusiveChoiceGrid
              name="homeSafetyConcerns"
              options={HOME_SAFETY_OPTIONS}
              value={values.homeSafetyConcerns || ""}
              canEdit={canEdit}
              onChange={(opt) => setValue("homeSafetyConcerns", opt)}
              columns={3}
            />
          </div>
        </Section>

        <Section icon={UsersIcon} title="11. Caregiver Assessment">
          <div className="space-y-3">
            <div>
              <SubHeading>Caregiver Availability</SubHeading>
              <ExclusiveChoiceGrid
                name="caregiverAvailability"
                options={CAREGIVER_AVAILABILITY_OPTIONS}
                value={values.caregiverAvailability || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("caregiverAvailability", opt)}
                columns={3}
              />
            </div>
            <div>
              <SubHeading>Ability to Assist</SubHeading>
              <ExclusiveChoiceGrid
                name="abilityToAssist"
                options={ABILITY_TO_ASSIST_OPTIONS}
                value={values.abilityToAssist || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("abilityToAssist", opt)}
                columns={3}
              />
            </div>
            <div>
              <SubHeading>Stress / Strain Indicators</SubHeading>
              <ExclusiveChoiceGrid
                name="stressStrainIndicators"
                options={STRESS_STRAIN_OPTIONS}
                value={values.stressStrainIndicators || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("stressStrainIndicators", opt)}
                columns={3}
              />
            </div>
          </div>
        </Section>

        <Section icon={FileCheckIcon} title="12. Financial Barriers to Care">
          <textarea
            rows={3}
            className={textareaClass}
            value={values.financialBarriersToCare || ""}
            disabled={!canEdit}
            placeholder="Describe financial barriers affecting care…"
            onChange={(e) =>
              setValue("financialBarriersToCare", e.target.value)
            }
          />
        </Section>

        <Section icon={HelpingIcon} title="13. Community Resources & Referrals">
          <CheckboxGrid
            options={COMMUNITY_RESOURCES_OPTIONS}
            values={values.communityResources}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("communityResources", opt)}
            columns={3}
          />
        </Section>

        <Section icon={ShieldCheckIcon} title="14. Advance Directives">
          <div className="space-y-2.5">
            {ADVANCE_DIRECTIVE_ROWS.map((row) => (
              <div
                key={row.key}
                className="rounded-md border border-slate-200 bg-slate-50/40 px-2.5 py-2"
              >
                <div className="text-[11px] font-semibold text-slate-700 mb-1.5">
                  {row.label}
                </div>
                <ExclusiveChoiceGrid
                  name={`advance-${row.key}`}
                  options={ADVANCE_DIRECTIVE_YES_NO}
                  value={advanceDirectiveDisplay(directives[row.key])}
                  canEdit={canEdit}
                  onChange={(opt) => setAdvanceDirective(row.key, opt)}
                  columns={2}
                />
              </div>
            ))}
          </div>
        </Section>

        <Section icon={MedicalNotesIcon} title="15. Patient/Caregiver Education">
          <textarea
            rows={3}
            className={textareaClass}
            value={values.patientCaregiverEducation || ""}
            disabled={!canEdit}
            placeholder="Document education provided to patient and/or caregiver…"
            onChange={(e) =>
              setValue("patientCaregiverEducation", e.target.value)
            }
          />
        </Section>

        <Section icon={TargetIcon} title="16. Progress Toward Goals">
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 border-b border-slate-100 bg-slate-50/70">
              <SubHeading>Goals</SubHeading>
              {canEdit ? (
                <button
                  type="button"
                  onClick={handleAddProgressGoal}
                  className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-semibold rounded border border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
                >
                  <PlusIcon size={12} />
                  Add Goal
                </button>
              ) : null}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-left text-[12px]">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50 text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-2.5 py-1.5 font-semibold w-8">#</th>
                    <th className="px-2.5 py-1.5 font-semibold">Description</th>
                    <th className="px-2.5 py-1.5 font-semibold w-36">Status</th>
                    <th className="px-2.5 py-1.5 font-semibold">Details</th>
                    {canEdit ? (
                      <th className="px-2.5 py-1.5 font-semibold w-10" />
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {(values.progressGoals || []).map((row, idx) => {
                    const statusClass =
                      PROGRESS_GOAL_STATUS_CLASS[row.status] || "text-slate-700";
                    return (
                      <tr
                        key={row.id}
                        className="border-b border-slate-100 last:border-0"
                      >
                        <td className="px-2.5 py-1.5 align-middle text-[11px] text-slate-500 font-semibold tabular-nums">
                          {idx + 1}
                        </td>
                        <td className="px-2.5 py-1.5 align-middle">
                          <input
                            type="text"
                            className={fieldClass}
                            value={row.description || ""}
                            disabled={!canEdit}
                            placeholder="Goal description…"
                            aria-label={`Goal ${idx + 1} description`}
                            onChange={(e) =>
                              updateProgressGoal(row.id, {
                                description: e.target.value,
                              })
                            }
                          />
                        </td>
                        <td className="px-2.5 py-1.5 align-middle w-36">
                          <select
                            className={`${fieldClass} ${statusClass}`}
                            value={row.status || ""}
                            disabled={!canEdit}
                            aria-label={`Goal ${idx + 1} status`}
                            onChange={(e) =>
                              updateProgressGoal(row.id, {
                                status: e.target.value,
                              })
                            }
                          >
                            <option value="">Select…</option>
                            {PROGRESS_GOAL_STATUS_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2.5 py-1.5 align-middle">
                          <input
                            type="text"
                            className={fieldClass}
                            value={row.details || ""}
                            disabled={!canEdit}
                            placeholder="Progress details…"
                            aria-label={`Goal ${idx + 1} details`}
                            onChange={(e) =>
                              updateProgressGoal(row.id, {
                                details: e.target.value,
                              })
                            }
                          />
                        </td>
                        {canEdit ? (
                          <td className="px-2.5 py-1.5 align-middle w-10">
                            <button
                              type="button"
                              disabled={(values.progressGoals || []).length <= 1}
                              onClick={() => handleRemoveProgressGoal(row.id)}
                              className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                              aria-label={`Remove goal ${idx + 1}`}
                            >
                              <DeleteIcon size={14} />
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Section>

        <Section icon={FileCheckIcon} title="17. Interventions Provided">
          <CheckboxGrid
            options={INTERVENTIONS_PROVIDED_OPTIONS}
            values={values.interventionsProvided}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("interventionsProvided", opt)}
            columns={4}
          />
        </Section>

        <Section icon={CalendarIcon} title="18. Plan for Next Visit">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2 items-end">
            <Field label="Frequency">
              <input
                type="text"
                className={fieldClass}
                value={values.planFrequency || ""}
                disabled={!canEdit}
                placeholder="e.g. 1x/week x 4 weeks"
                onChange={(e) => setValue("planFrequency", e.target.value)}
              />
            </Field>
            <Field label="Next Scheduled Date">
              <DateFieldInput
                name="nextScheduledDate"
                value={values.nextScheduledDate || ""}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("nextScheduledDate", v)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={NotesIcon} title="19. Additional Notes">
          <textarea
            rows={4}
            className={textareaClass}
            value={values.additionalNotes || ""}
            disabled={!canEdit}
            placeholder="Additional notes…"
            onChange={(e) => setValue("additionalNotes", e.target.value)}
          />
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
          clinicianSectionTitle="Authentication & Certification"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

MswVisitNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default MswVisitNoteForm;
